import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const STRENGTH_ICONS = [
  "ri-bar-chart-box-line",
  "ri-global-line",
  "ri-shopping-bag-3-line",
  "ri-truck-line",
  "ri-advertisement-line",
];

export default function AboutPage() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.08 }
    );
    const children = document.querySelectorAll(".animate-on-scroll");
    children.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-[#1a2332]">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Tokyo%20city%20at%20dusk%20with%20glowing%20city%20lights%20and%20skyscrapers%2C%20dramatic%20wide%20angle%20shot%2C%20deep%20navy%20blue%20sky%20with%20warm%20amber%20city%20glow%2C%20cinematic%20atmosphere%2C%20ultra%20high%20quality%20photography%2C%20no%20people&width=1600&height=900&seq=about-hero-bg&orientation=landscape"
            alt="Tokyo"
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a2332] via-[#1a2332]/60 to-transparent"></div>
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-40">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white/80 transition-colors mb-12 tracking-widest uppercase"
            >
              <i className="ri-arrow-left-line"></i>
              <span>{t("services.backToHome")}</span>
            </Link>

            <div className="flex items-end justify-between flex-wrap gap-8">
              <div>
                <p className="text-xs text-white/40 tracking-[0.4em] uppercase mb-5">
                  ABOUT US / オーナー紹介
                </p>
                <h1 className="text-6xl md:text-8xl lg:text-[110px] font-black text-white leading-[0.95] tracking-tight">
                  {t("aboutPage.title")}
                </h1>
              </div>
              <p className="text-white/50 text-sm max-w-xs leading-relaxed pb-2">
                {t("aboutPage.heroSub")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission Statement ── */}
      <section className="py-24 md:py-36 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <div className="flex items-center gap-4 mb-16">
              <div className="w-8 h-px bg-[#1a2332]"></div>
              <p className="text-xs text-gray-400 tracking-[0.4em] uppercase">
                {t("aboutPage.missionLabel")}
              </p>
            </div>

            {/* Big quote */}
            <div className="mb-20">
              <p className="text-3xl md:text-5xl lg:text-6xl font-black text-[#1a2332] leading-[1.1] tracking-tight max-w-4xl">
                {t("aboutPage.missionTitle")}
              </p>
            </div>

            {/* Two-column body */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 border-t border-gray-100 pt-12">
              <div className="space-y-5 text-gray-600 text-base leading-[1.9]">
                <p>{t("aboutPage.missionText1")}</p>
                <p>{t("aboutPage.missionText2")}</p>
                <p>{t("aboutPage.missionText3")}</p>
              </div>
              <div className="space-y-5 text-gray-600 text-base leading-[1.9]">
                <p>{t("aboutPage.missionText4")}</p>
                <p>{t("aboutPage.missionText5")}</p>
                <p className="font-semibold text-[#1a2332]">{t("aboutPage.missionCta")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Full-bleed image break ── */}
      <div className="w-full h-[50vh] md:h-[60vh] overflow-hidden relative">
        <img
          src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20view%20of%20Tokyo%20business%20district%20Shinjuku%20at%20golden%20hour%2C%20modern%20skyscrapers%20reflecting%20warm%20sunset%20light%2C%20dramatic%20clouds%2C%20cinematic%20landscape%20photography%2C%20no%20text%2C%20ultra%20wide%20shot&width=1600&height=700&seq=about-fullbleed&orientation=landscape"
          alt="Tokyo Business District"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#1a2332]/20"></div>
        <div className="absolute bottom-8 right-10 text-white/30 text-xs tracking-[0.4em] uppercase">
          SHOP-MK
        </div>
      </div>

      {/* ── Strengths ── */}
      <section className="py-24 md:py-36 bg-[#1a2332]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <div className="flex items-center gap-4 mb-16">
              <div className="w-8 h-px bg-white/30"></div>
              <p className="text-xs text-white/40 tracking-[0.4em] uppercase">
                {t("aboutPage.strengthsLabel")}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                  {t("aboutPage.strengthsTitle")}
                </h2>
                <p className="text-white/50 text-sm leading-relaxed max-w-sm">
                  {t("aboutPage.strengthSubtitle")}
                </p>
              </div>

              <div className="divide-y divide-white/10">
                {STRENGTH_ICONS.map((icon, idx) => (
                  <div key={idx} className="flex items-center gap-6 py-5 group">
                    <span className="text-xs text-white/25 font-mono w-6 flex-shrink-0">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${icon} text-white/50 text-lg group-hover:text-white transition-colors`}></i>
                    </div>
                    <span className="text-white/80 text-sm group-hover:text-white transition-colors">
                      {t(`strengths.s${idx + 1}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Company Info Table ── */}
      <section className="py-24 md:py-36 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <div className="flex items-center gap-4 mb-16">
              <div className="w-8 h-px bg-[#1a2332]"></div>
              <p className="text-xs text-gray-400 tracking-[0.4em] uppercase">
                COMPANY PROFILE
              </p>
            </div>

            <div className="border-t border-gray-200">
              {[
                { label: t("aboutPage.info.companyName"), value: t("aboutPage.info.companyNameValue") },
                { label: t("aboutPage.info.established"), value: t("aboutPage.info.establishedValue") },
                { label: t("aboutPage.info.representative"), value: t("aboutPage.info.representativeValue") },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex flex-col sm:flex-row sm:items-center border-b border-gray-100 py-5 md:py-6"
                >
                  <div className="w-full sm:w-40 md:w-48 mb-2 sm:mb-0">
                    <span className="text-sm font-semibold text-[#1a2332]">{item.label}</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-gray-600 leading-relaxed">{item.value}</span>
                  </div>
                </div>
              ))}

              {/* 代表者紹介 */}
              <div className="border-b border-gray-100 py-8 md:py-10">
                <div className="flex flex-col sm:flex-row sm:items-start gap-0">
                  <div className="w-full sm:w-40 md:w-48 mb-4 sm:mb-0 pt-1">
                    <span className="text-sm font-semibold text-[#1a2332]">{t("aboutPage.info.repProfile")}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row gap-8 items-start">
                      {/* Profile image */}
                      <div className="flex-shrink-0">
                        <div className="w-36 h-44 md:w-40 md:h-48 overflow-hidden rounded-lg bg-gray-100">
                          <img
                            src="https://storage.readdy-site.link/project_files/fcdfbc47-604d-46a5-8fcd-de9b12e9c902/cab26732-0401-4b42-98bd-ec12b56064d9_unnamed.jpg?v=1841b165d92a8d8b7aa2b2a57c63eca9"
                            alt="代表者 木佐 誠宏"
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">{t("aboutPage.info.representativeValue")}</p>
                      </div>
                      {/* Profile text */}
                      <div className="flex-1 space-y-4 text-sm text-gray-600 leading-[1.9]">
                        <p>{t("aboutPage.info.repText1")}</p>
                        <p>{t("aboutPage.info.repText2")}</p>
                        <p>{t("aboutPage.info.repText3")}</p>
                        <p>{t("aboutPage.info.repText4")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 年商 */}
              <div className="flex flex-col sm:flex-row sm:items-center border-b border-gray-100 py-5 md:py-6">
                <div className="w-full sm:w-40 md:w-48 mb-2 sm:mb-0">
                  <span className="text-sm font-semibold text-[#1a2332]">{t("aboutPage.info.revenue")}</span>
                </div>
                <div className="flex-1">
                  <span className="text-sm text-gray-600 leading-relaxed">{t("aboutPage.info.revenueValue")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 md:py-32 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
              <div>
                <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-5">CONTACT</p>
                <h2 className="text-3xl md:text-5xl font-black text-[#1a2332] leading-tight">
                  {t("aboutPage.ctaTitle")}
                </h2>
                <p className="text-gray-500 mt-4 max-w-md leading-relaxed">
                  {t("aboutPage.ctaText")}
                </p>
              </div>
              <Link
                to="/"
                onClick={() => setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 100)}
                className="inline-flex items-center gap-3 bg-[#1a2332] text-white font-medium text-sm px-8 py-4 rounded-full hover:bg-[#2d3f55] transition-colors duration-300 cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <span>{t("aboutPage.ctaButton")}</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}